import React, { useEffect } from 'react';
import { StyleSheet, View, Text, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { BaseAdminScreen, ResidentDetailsHeader, ResidentDetailsInfo, ResidentEditForm, AdminErrorBoundary } from '@/components';
import { ADMIN_COLORS } from '@/constants/admin';
import { useErrorToast } from '@/contexts/admin/ToastContext';
import { useErrorToastOnce } from '@/hooks/common/useErrorToastOnce';
import AdminErrorState from '@/components/common/admin/mobile/AdminErrorState';
import { useResidentDetails } from '@/hooks/admin/useResidentDetails';
import { RESIDENT_DETAILS_TABS } from '@/constants/admin/residents';

type ResidentDetailsProps = StackScreenProps<AdminStackParamList, 'ResidentDetails'>;

export const ResidentDetails: React.FC<ResidentDetailsProps> = ({
  navigation,
  route,
}) => {
  const { accountId, leaseId, mode } = route.params;
  const { showApiError } = useErrorToast();
  
  const {
    residentDetails,
    isLoading,
    error,
    enhancedError,
    refetch,
    retryCount,
    canRetry,
  } = useResidentDetails({ accountId, leaseId, mode: mode || 'resident' });

  // Show toast for API errors
  useErrorToastOnce(enhancedError, showApiError);

  // Determine which tabs to show based on data
  const hasResioAccount = residentDetails?.accountId ? true : false;
  const tabs = [RESIDENT_DETAILS_TABS.GENERAL, RESIDENT_DETAILS_TABS.EDIT];
// const tabs = hasResioAccount 
//   ? ['General', 'Edit User', 'Payments'] 
//   : ['General', 'Edit User'];
  
  const [activeTab, setActiveTab] = React.useState(tabs[0]);

  const handleClose = () => {
    navigation.goBack();
  };

  const renderContent = () => {
    if (isLoading && !residentDetails) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={ADMIN_COLORS.PRIMARY} />
          <Text style={styles.loadingText}>Loading resident details...</Text>
        </View>
      );
    }

    if (error && !residentDetails) {
      return (
        <AdminErrorState
          title="Unable to Load Resident Details"
          message={enhancedError?.userFriendlyMessage || 'Something went wrong while loading the resident details.'}
          retryCount={retryCount}
          canRetry={canRetry}
          onRetry={refetch}
        />
      );
    }

    if (!residentDetails) {
      return (
        <View style={styles.centerContainer}>
          <Text style={styles.noDataText}>No resident details found</Text>
        </View>
      );
    }

    return (
      <>
        <ResidentDetailsHeader 
          resident={residentDetails}
          onClose={handleClose}
        />
        
        {/* Tab Bar */}
        <View style={styles.tabContainer}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, activeTab === tab && styles.activeTab]}
              onPress={() => setActiveTab(tab)}
              activeOpacity={0.7}
            >
              <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Tab Content */}
        {activeTab === RESIDENT_DETAILS_TABS.GENERAL ? (
          <ScrollView style={styles.contentContainer} showsVerticalScrollIndicator={false}>
            <ResidentDetailsInfo resident={residentDetails} leaseCustomerId={accountId} leaseId={leaseId} />
            {!hasResioAccount && (
              <View style={styles.noAccountMessage}>
                <Text style={styles.noAccountText}>User has no associated resio account</Text>
              </View>
            )}
          </ScrollView>
        ) : (
          <View style={styles.editFormContainer}>
            <ResidentEditForm 
              accountId={accountId} 
              residentDetails={residentDetails}
            />
          </View>
        )}
      </>
    );
  };

  return (
    <AdminErrorBoundary>
      <BaseAdminScreen
        screenName="ResidentDetails"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <View style={styles.container}>
          {renderContent()}
        </View>
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  baseScreenContent: {
    paddingHorizontal: 0,
    paddingTop: 0,
    paddingBottom: 0,
    flexGrow: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  noDataText: {
    fontSize: 16,
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  tabContainer: {
    backgroundColor: ADMIN_COLORS.SURFACE,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.BORDER,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    gap: 12,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: 'transparent',
    alignItems: 'center',
  },
  activeTab: {
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_SECONDARY,
  },
  activeTabText: {
    color: '#FFFFFF',
  },
  contentContainer: {
    flex: 1,
  },
  editFormContainer: {
    flex: 1,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
  noAccountMessage: {
    padding: 20,
    alignItems: 'center',
  },
  noAccountText: {
    fontSize: 16,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT_PRIMARY,
  },
});
